import { ANALYTICS_DAY_OR_COARSER_UNITS } from "./analytics-query-constants";
import type {
  AnalyticsAnyUnitFunctionName,
  AnalyticsBooleanConnectiveName,
  AnalyticsBooleanPredicateName,
  AnalyticsDayOrCoarserFunctionName,
  AnalyticsFunctionName,
  AnalyticsFunctionSignature,
  AnalyticsUnitlessFunctionName,
} from "./analytics-query-function-types";
import {
  AnalyticsMeasureTypes,
  AnalyticsOrderedTypes,
  AnalyticsRoundableTypes,
} from "./analytics-query-semantic-types";

/**
 * Arity, argument and result typing, and absence contract per function; a new operation is one row, not a node kind.
 * The schema reads names, argument-count shape and `unit`; resolution reads the argument and result rules, constant slots included.
 */

export const AnalyticsFunctionRegistry = {
  // arithmetic
  add: {
    params: [
      { name: "a", rule: { kind: "any" } },
      { name: "b", rule: { kind: "any" } },
    ],
    result: { kind: "algebra", op: "add" },
    absence: "propagate",
    description: "a plus b",
  },
  subtract: {
    params: [
      { name: "a", rule: { kind: "any" } },
      { name: "b", rule: { kind: "any" } },
    ],
    result: { kind: "algebra", op: "subtract" },
    absence: "propagate",
    description: "a minus b",
  },
  multiply: {
    params: [
      { name: "a", rule: { kind: "any" } },
      { name: "b", rule: { kind: "any" } },
    ],
    result: { kind: "algebra", op: "multiply" },
    absence: "propagate",
    description: "a times b",
  },
  /** Division by zero is absent; for zero, fallback to a 0 of the quotient's type (a ratio for two quantities). */
  divide: {
    params: [
      { name: "a", rule: { kind: "any" } },
      { name: "b", rule: { kind: "any" } },
    ],
    result: { kind: "algebra", op: "divide" },
    absence: "propagate",
    description: "a divided by b",
  },
  negate: {
    params: [
      { name: "a", rule: { kind: "typed", types: AnalyticsMeasureTypes } },
    ],
    result: { kind: "arg", index: 0 },
    absence: "propagate",
    description: "minus a",
  },
  absolute: {
    params: [
      { name: "a", rule: { kind: "typed", types: AnalyticsMeasureTypes } },
    ],
    result: { kind: "arg", index: 0 },
    absence: "propagate",
    description: "a without its sign",
  },
  round: {
    params: [
      { name: "a", rule: { kind: "typed", types: AnalyticsRoundableTypes } },
      { name: "places", rule: { kind: "constant_integer", min: 0, max: 10 } },
    ],
    result: { kind: "arg", index: 0 },
    absence: "propagate",
    description: "a rounded to places",
  },
  floor: {
    params: [
      { name: "a", rule: { kind: "typed", types: AnalyticsRoundableTypes } },
    ],
    result: { kind: "arg", index: 0 },
    absence: "propagate",
    description: "a rounded down",
  },
  ceiling: {
    params: [
      { name: "a", rule: { kind: "typed", types: AnalyticsRoundableTypes } },
    ],
    result: { kind: "arg", index: 0 },
    absence: "propagate",
    description: "a rounded up",
  },
  /** A zero divisor is absent, as for `divide`. */
  modulo: {
    params: [
      { name: "a", rule: { kind: "typed", types: AnalyticsMeasureTypes } },
      { name: "b", rule: { kind: "typed", types: AnalyticsMeasureTypes } },
    ],
    result: { kind: "arg", index: 0 },
    absence: "propagate",
    description: "the remainder of a divided by b, with the sign of a",
  },
  power: {
    params: [
      { name: "a", rule: { kind: "typed", types: AnalyticsMeasureTypes } },
      { name: "b", rule: { kind: "typed", types: AnalyticsMeasureTypes } },
    ],
    result: { kind: "fixed", type: "quantity" },
    absence: "propagate",
    description: "a raised to the power b",
  },
  greatest: {
    params: [{ name: "values", rule: { kind: "uniform" } }],
    variadic: true,
    result: { kind: "uniform" },
    absence: "absorb",
    description: "the largest of the values",
  },
  least: {
    params: [{ name: "values", rule: { kind: "uniform" } }],
    variadic: true,
    result: { kind: "uniform" },
    absence: "absorb",
    description: "the smallest of the values",
  },

  // comparison (two-valued for the author)
  equals: {
    params: [
      { name: "a", rule: { kind: "uniform" } },
      { name: "b", rule: { kind: "uniform" } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "a is b",
  },
  differs: {
    params: [
      { name: "a", rule: { kind: "uniform" } },
      { name: "b", rule: { kind: "uniform" } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "a is not b",
  },
  greater: {
    params: [
      { name: "a", rule: { kind: "uniform", types: AnalyticsOrderedTypes } },
      { name: "b", rule: { kind: "uniform" } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "a is more than b",
  },
  less: {
    params: [
      { name: "a", rule: { kind: "uniform", types: AnalyticsOrderedTypes } },
      { name: "b", rule: { kind: "uniform" } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "a is less than b",
  },
  at_least: {
    params: [
      { name: "a", rule: { kind: "uniform", types: AnalyticsOrderedTypes } },
      { name: "b", rule: { kind: "uniform" } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "a is b or more",
  },
  at_most: {
    params: [
      { name: "a", rule: { kind: "uniform", types: AnalyticsOrderedTypes } },
      { name: "b", rule: { kind: "uniform" } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "a is b or less",
  },
  between: {
    params: [
      { name: "a", rule: { kind: "uniform" } },
      { name: "low", rule: { kind: "uniform" } },
      { name: "high", rule: { kind: "uniform" } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "low <= a <= high",
  },
  one_of: {
    params: [
      { name: "a", rule: { kind: "uniform" } },
      { name: "options", rule: { kind: "uniform" } },
    ],
    variadic: true,
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "a is one of the options",
  },
  present: {
    params: [{ name: "a", rule: { kind: "any" } }],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "a has a value",
  },
  absent: {
    params: [{ name: "a", rule: { kind: "any" } }],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "a has no value",
  },
  includes: {
    params: [
      { name: "list", rule: { kind: "typed", types: ["list"] } },
      { name: "item", rule: { kind: "any" } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "the list contains the item",
  },

  // connectives
  all_of: {
    params: [
      { name: "conditions", rule: { kind: "typed", types: ["boolean"] } },
    ],
    variadic: true,
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "every condition holds",
  },
  any_of: {
    params: [
      { name: "conditions", rule: { kind: "typed", types: ["boolean"] } },
    ],
    variadic: true,
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "at least one condition holds",
  },
  not: {
    params: [
      { name: "condition", rule: { kind: "typed", types: ["boolean"] } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "the condition does not hold",
  },

  // absence handling (the only author-facing absence concepts besides present/absent)
  fallback: {
    params: [{ name: "values", rule: { kind: "uniform" } }],
    variadic: true,
    result: { kind: "uniform" },
    absence: "absorb",
    description: "the first value that is present",
  },

  // text
  concat: {
    params: [{ name: "parts", rule: { kind: "any" } }],
    variadic: true,
    result: { kind: "fixed", type: "text" },
    absence: "absorb",
    description: "the parts joined as text; non-text parts are rendered",
  },
  lowercase: {
    params: [
      { name: "text", rule: { kind: "typed", types: ["text", "category"] } },
    ],
    result: { kind: "fixed", type: "text" },
    absence: "propagate",
    description: "text in lower case",
  },
  uppercase: {
    params: [
      { name: "text", rule: { kind: "typed", types: ["text", "category"] } },
    ],
    result: { kind: "fixed", type: "text" },
    absence: "propagate",
    description: "text in upper case",
  },
  trim: {
    params: [{ name: "text", rule: { kind: "typed", types: ["text"] } }],
    result: { kind: "fixed", type: "text" },
    absence: "propagate",
    description: "text without surrounding spaces",
  },
  length: {
    params: [{ name: "text", rule: { kind: "typed", types: ["text"] } }],
    result: { kind: "fixed", type: "quantity" },
    absence: "propagate",
    description: "number of characters",
  },
  contains: {
    params: [
      { name: "text", rule: { kind: "typed", types: ["text", "category"] } },
      { name: "part", rule: { kind: "typed", types: ["text"] } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "text contains part (case-insensitive)",
  },
  starts_with: {
    params: [
      { name: "text", rule: { kind: "typed", types: ["text", "category"] } },
      { name: "part", rule: { kind: "typed", types: ["text"] } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "text begins with part",
  },
  ends_with: {
    params: [
      { name: "text", rule: { kind: "typed", types: ["text", "category"] } },
      { name: "part", rule: { kind: "typed", types: ["text"] } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "text ends with part",
  },
  matches: {
    params: [
      { name: "text", rule: { kind: "typed", types: ["text"] } },
      { name: "pattern", rule: { kind: "constant_text" } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description:
      "text matches the pattern (portable regex subset, SQL:2016 LIKE_REGEX)",
  },
  /** SQL LIKE as written: `%` and `_` are wildcards and no character escapes them. */
  like: {
    params: [
      { name: "text", rule: { kind: "typed", types: ["text"] } },
      { name: "pattern", rule: { kind: "constant_text" } },
    ],
    result: { kind: "fixed", type: "boolean" },
    absence: "boolean",
    description: "text matches the LIKE pattern, case-sensitively",
  },
  segment: {
    params: [
      { name: "text", rule: { kind: "typed", types: ["text"] } },
      { name: "separator", rule: { kind: "typed", types: ["text"] } },
      { name: "index", rule: { kind: "constant_integer", min: 1 } },
    ],
    result: { kind: "fixed", type: "text" },
    absence: "propagate",
    description:
      "the index-th piece of text split on separator (1 is the first piece); absent past the end",
  },
  substring: {
    params: [
      { name: "text", rule: { kind: "typed", types: ["text"] } },
      { name: "from", rule: { kind: "typed", types: ["quantity"] } },
      { name: "length", rule: { kind: "constant_integer", min: 0 } },
    ],
    result: { kind: "fixed", type: "text" },
    absence: "propagate",
    description:
      "length characters of text from position from, a whole number (1 is the first; positions before 1 hold nothing)",
  },
  replace: {
    params: [
      { name: "text", rule: { kind: "typed", types: ["text"] } },
      { name: "find", rule: { kind: "typed", types: ["text"] } },
      { name: "with", rule: { kind: "typed", types: ["text"] } },
    ],
    result: { kind: "fixed", type: "text" },
    absence: "propagate",
    description: "text with every find replaced by with",
  },
  parse_quantity: {
    params: [{ name: "text", rule: { kind: "typed", types: ["text"] } }],
    result: { kind: "fixed", type: "quantity" },
    absence: "propagate",
    description: "text read as a number; absent when it is not one",
  },
  parse_date: {
    params: [{ name: "text", rule: { kind: "typed", types: ["text"] } }],
    result: { kind: "fixed", type: "date" },
    absence: "propagate",
    description: "text read as an ISO date; absent when it is not one",
  },
  join_text: {
    params: [
      { name: "list", rule: { kind: "typed", types: ["list"] } },
      { name: "separator", rule: { kind: "constant_text" } },
    ],
    result: { kind: "fixed", type: "text" },
    absence: "absorb",
    description: "list items joined into one text",
  },
  item_count: {
    params: [{ name: "list", rule: { kind: "typed", types: ["list"] } }],
    result: { kind: "fixed", type: "quantity" },
    absence: "propagate",
    description: "number of items in the list",
  },

  // time (all in the report time zone by compiler contract)
  today: {
    params: [],
    result: { kind: "fixed", type: "date" },
    absence: "propagate",
    description: "the current date",
  },
  now: {
    params: [],
    result: { kind: "fixed", type: "datetime" },
    absence: "propagate",
    description: "the current instant",
  },
  bucket: {
    params: [
      { name: "time", rule: { kind: "typed", types: ["date", "datetime"] } },
    ],
    unit: "required",
    units: ANALYTICS_DAY_OR_COARSER_UNITS,
    result: { kind: "fixed", type: "date" },
    absence: "propagate",
    description: "the first day of the unit containing time",
  },
  end_of: {
    params: [
      { name: "time", rule: { kind: "typed", types: ["date", "datetime"] } },
    ],
    unit: "required",
    units: ANALYTICS_DAY_OR_COARSER_UNITS,
    result: { kind: "fixed", type: "date" },
    absence: "propagate",
    description: "the last day of the unit containing time",
  },
  shift: {
    params: [
      { name: "time", rule: { kind: "typed", types: ["date", "datetime"] } },
      { name: "amount", rule: { kind: "typed", types: ["quantity"] } },
    ],
    unit: "required",
    result: { kind: "arg", index: 0 },
    absence: "propagate",
    description: "time moved by amount units",
  },
  elapsed: {
    params: [
      { name: "from", rule: { kind: "typed", types: ["date", "datetime"] } },
      { name: "to", rule: { kind: "typed", types: ["date", "datetime"] } },
    ],
    unit: "required",
    result: { kind: "fixed", type: "duration" },
    absence: "propagate",
    description: "whole units from from to to",
  },
  /** Unlike `elapsed`, exact: it reads both instants in UTC and keeps the fraction, so a caller can round it. */
  elapsed_milliseconds: {
    params: [
      { name: "from", rule: { kind: "typed", types: ["datetime"] } },
      { name: "to", rule: { kind: "typed", types: ["datetime"] } },
    ],
    result: { kind: "fixed", type: "quantity" },
    absence: "propagate",
    description:
      "milliseconds from from to to, exact to the microsecond (negative when to is earlier)",
  },
  part: {
    params: [
      { name: "time", rule: { kind: "typed", types: ["date", "datetime"] } },
    ],
    unit: "required",
    result: { kind: "fixed", type: "quantity" },
    absence: "propagate",
    description: "the unit component of time (month number, year, ...)",
  },
  to_date: {
    params: [{ name: "time", rule: { kind: "typed", types: ["datetime"] } }],
    result: { kind: "fixed", type: "date" },
    absence: "propagate",
    description: "the calendar date of the instant",
  },

  /** Conversions. An unconvertible value is an error at run time, as SQL CAST is. */
  cast_text: {
    params: [{ name: "value", rule: { kind: "any" } }],
    result: { kind: "fixed", type: "text" },
    absence: "propagate",
    description: "value converted to text",
  },
  cast_number: {
    params: [{ name: "value", rule: { kind: "any" } }],
    result: { kind: "fixed", type: "quantity" },
    absence: "propagate",
    description: "value converted to a whole number, rounding any fraction",
  },
  cast_boolean: {
    params: [{ name: "value", rule: { kind: "any" } }],
    result: { kind: "fixed", type: "boolean" },
    absence: "propagate",
    description: "value converted to true or false",
  },
  cast_date: {
    params: [{ name: "value", rule: { kind: "any" } }],
    result: { kind: "fixed", type: "date" },
    absence: "propagate",
    description: "value converted to a date",
  },
  cast_datetime: {
    params: [{ name: "value", rule: { kind: "any" } }],
    result: { kind: "fixed", type: "datetime" },
    absence: "propagate",
    description: "value converted to an instant",
  },
} as const satisfies Record<string, AnalyticsFunctionSignature>;

/** `Object.keys` widens to `string[]`; the registry is a closed const object, so its keys are exactly the function names. */
export const ANALYTICS_FUNCTION_NAMES: readonly AnalyticsFunctionName[] =
  Object.keys(AnalyticsFunctionRegistry).filter(
    (name): name is AnalyticsFunctionName => name in AnalyticsFunctionRegistry,
  );

/** Widens a registry row to the signature contract so optional slots (`unit`, `units`) are addressable. */
export function analyticsFunctionSignature(
  fn: AnalyticsFunctionName,
): AnalyticsFunctionSignature {
  return AnalyticsFunctionRegistry[fn];
}

/**
 * Unit slot, three shapes derived from the rows. Call arms use the finer
 * groups below, so `unit` is present exactly on the functions that take one.
 */
/** `bucket`, `end_of`: a unit is required and only day-or-coarser units are legal. */
export const ANALYTICS_DAY_OR_COARSER_FUNCTION_NAMES: readonly AnalyticsDayOrCoarserFunctionName[] =
  ANALYTICS_FUNCTION_NAMES.filter(
    (fn): fn is AnalyticsDayOrCoarserFunctionName =>
      analyticsFunctionSignature(fn).units !== undefined,
  );

export const ANALYTICS_ANY_UNIT_FUNCTION_NAMES: readonly AnalyticsAnyUnitFunctionName[] =
  ANALYTICS_FUNCTION_NAMES.filter(
    (fn): fn is AnalyticsAnyUnitFunctionName =>
      analyticsFunctionSignature(fn).unit === "required" &&
      analyticsFunctionSignature(fn).units === undefined,
  );

export const ANALYTICS_UNITLESS_FUNCTION_NAMES: readonly AnalyticsUnitlessFunctionName[] =
  ANALYTICS_FUNCTION_NAMES.filter(
    (fn): fn is AnalyticsUnitlessFunctionName =>
      analyticsFunctionSignature(fn).unit !== "required",
  );

/** Narrows to the arm whose unit enum is restricted; the same predicate the group filters use. */
export const isAnalyticsDayOrCoarserFunction = (
  fn: AnalyticsFunctionName,
): fn is AnalyticsDayOrCoarserFunctionName =>
  analyticsFunctionSignature(fn).units !== undefined;

function analyticsFunctionNameIncludes<T extends AnalyticsFunctionName>(
  names: readonly T[],
  fn: AnalyticsFunctionName,
): fn is T {
  return (names as readonly AnalyticsFunctionName[]).includes(fn);
}

export const ANALYTICS_BOOLEAN_CONNECTIVE_NAMES = [
  "all_of",
  "any_of",
  "not",
] as const satisfies readonly AnalyticsFunctionName[];

export const ANALYTICS_BOOLEAN_PREDICATE_NAMES: readonly AnalyticsBooleanPredicateName[] =
  ANALYTICS_FUNCTION_NAMES.filter((fn): fn is AnalyticsBooleanPredicateName => {
    const result = analyticsFunctionSignature(fn).result;
    return (
      result.kind === "fixed" &&
      result.type === "boolean" &&
      !analyticsFunctionNameIncludes(ANALYTICS_BOOLEAN_CONNECTIVE_NAMES, fn)
    );
  });

export const isAnalyticsBooleanConnective = (
  fn: AnalyticsFunctionName,
): fn is AnalyticsBooleanConnectiveName =>
  analyticsFunctionNameIncludes(ANALYTICS_BOOLEAN_CONNECTIVE_NAMES, fn);

export const isAnalyticsBooleanPredicate = (
  fn: AnalyticsFunctionName,
): fn is AnalyticsBooleanPredicateName =>
  analyticsFunctionNameIncludes(ANALYTICS_BOOLEAN_PREDICATE_NAMES, fn);
